import { Inject, Injectable, OnDestroy } from "@angular/core"
import { Subject, timer, fromEvent, merge, Observable, Subscription } from "rxjs"
import { PrivacyRequestService } from "../privacy-request/privacy-request.service"
import { takeUntil, skipUntil, repeat, switchMap, filter, map } from "rxjs/operators"
import { AppConfig, APP_CONFIG } from "src/app.config"

@Injectable({
  providedIn: "root",
})
export class TokenManagementService implements OnDestroy {
  private config: AppConfig
  private idleDialogTimer: Observable<number>
  private _restartIdleTimer = new Subject<void>()
  private shouldSignOut = false
  private lastActivityTime: number = Date.now()
  private freezeSubscription: Subscription
  private activitySubscription: Subscription
  public refreshTokenTimer: Observable<number>
  public refreshTokenTimer$
  public discardedFrozenPageTimer: Observable<number>
  public discardedFrozenPageTimer$

  constructor(@Inject(APP_CONFIG) config: AppConfig, private privacyRequestService: PrivacyRequestService) {
    this.config = config
    this.idleDialogTimer = timer(this.config.idleTimer, this.config.idleTimer)
    this.refreshTokenTimer = timer(this.config.refreshTimer, this.config.refreshTimer)
    this.refreshTokenTimer$ = this.refreshTokenTimer.pipe(
      skipUntil(this.privacyRequestService.publishRequestEligibility$),
      filter(() => !this.shouldSignOutImmediately())
    )
    this.discardedFrozenPageTimer = timer(this.config.discardedFrozenPageTimer, this.config.discardedFrozenPageTimer)
    this.discardedFrozenPageTimer$ = this.discardedFrozenPageTimer.pipe(
      skipUntil(this.privacyRequestService.publishRequestEligibility$),
      filter(() => this.shouldSignOutImmediately())
    )
    
    // Listen for freeze event
    this.freezeSubscription = fromEvent(document, 'freeze').subscribe(() => {
      this.shouldSignOut = true
    })

    // Track user activity to update lastActivityTime
    this.activitySubscription = this.eventStream$.subscribe(() => {
      this.lastActivityTime = Date.now()
    })
  }

  public eventStream$ = merge(
    fromEvent(document, "keydown"),
    fromEvent(document, "scroll"),
    fromEvent(document, "mouseover")
  )

  public idleDialogTimer$ = this.privacyRequestService.publishRequestEligibility$.pipe(
    switchMap(() => this.idleDialogTimer.pipe(takeUntil(merge(this._restartIdleTimer, this.eventStream$)), repeat())),
    filter(() => !this.shouldSignOutImmediately())
  )

  /**
   * Check if user should be signed out immediately without showing the idle dialog
   * @returns true if user exceeded timeout, page was frozen/discarded, or other sign-out condition met
   */
  private shouldSignOutImmediately(): boolean {
    // Check if page was discarded and restored
    if ((document as any).wasDiscarded) {
      return true
    }

    // Check if page was frozen or user exceeded timeout
    if (this.shouldSignOut) {
      return true
    }

    // Check if page is currently frozen/discarded - safeguard since JS will stop executing once tab is fully frozen/discarded
    const lifecycleState = (document as any).lifecycle?.state
    if (lifecycleState === 'frozen' || lifecycleState === 'discarded') {
      return true
    }

    // Real-time check: Sign out at 10+ min if page is hidden (user away from tab)
    if (document.hidden) {
      const timeSinceActivity = Date.now() - this.lastActivityTime
      if (timeSinceActivity > this.config.idleTimer + this.config.sessionDialogTimer) {
        return true
      }
    }

    return false
  }

  restartIdleTimer(): void {
    this._restartIdleTimer.next()
    this.lastActivityTime = Date.now()
    this.shouldSignOut = false
  }

  ngOnDestroy(): void {
    this.freezeSubscription?.unsubscribe()
    this.activitySubscription?.unsubscribe()
  }
} 